import { faCalendarPlus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { classnames, DatePicker, SingleFilterDropDown, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import {
    Button,
    ButtonBar,
    ButtonVariant,
    Card,
    Checkbox,
    ConfirmButton,
    Dialog,
} from '@ygtechnologies/rubus-ui';
import { Input } from '@/components/generic-controls';
import { orderBy } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { deleteMultipleInternalMeetings, MeetingBooking, MeetingType, postMultipleInternalMeetings } from '@/services/booking';
import { FilterItem, TranslatableText } from '@/types';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { numberToClock, toDateOnly } from '@/utils/date';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ResidentialUnitMeetingData } from '../../MeetingApartments/MeetingApartments';
import theme from './DateChangeDialog.module.scss';
import { OnChangeValue } from 'react-select';

interface DateChangeDialogProps {
    data: ResidentialUnitMeetingData[];
    activeTab: MeetingType;
    show: boolean;
    onSend(): void;
    onClose(): void;
    onFilterChange(row: ResidentialUnitMeetingData, unitId: boolean): void;
    onCancel?(): void;
    confirmButtonColor?: ButtonVariant;
    cancelButtonColor?: ButtonVariant;
    confirmButtonText?: TranslatableText;
    cancelButtonText?: TranslatableText;
    /**
     * This is the "x" on top right
     */
    onCloseClick?(): void;
}

const DateChangeDialog: React.FC<DateChangeDialogProps> = ({
    data,
    activeTab,
    show,
    onSend,
    onClose,
    onFilterChange,
    onCancel,
    onCloseClick = onCancel,
    confirmButtonColor = 'primary',
    cancelButtonColor = 'tertiary',
    confirmButtonText = <Trans>Confirm</Trans>,
    cancelButtonText = <Trans>Cancel</Trans>,
}) => {
    const [showMore, setShowMore] = useState<boolean>(false);
    const [flipper, setFlipper] = useState<boolean>(false);
    const [onlyDates, setOnlyDates] = useState<boolean>(false);
    const [localData, setLocalData] = useState<ResidentialUnitMeetingData[]>(data);
    const [saving, setSaving] = useState(false);
    const [meetingsToBeDeleted, setMeetingsToBeDeleted] = useState<number[]>([]);

    useEffect(() => {
        if (!localData) {
            setLocalData(data);
        } else {
            const newArray = localData.map((x) => {
                const item = data.find((y) => y.id === x.id);
                if (item) {
                    if ((item.isChecked && !x.isChecked) || (!item.isChecked && x.isChecked)) {
                        return item;
                    }
                }
                return x;
            });
            setLocalData(newArray);
        }
    }, [data]);

    useEffect(() => {
        setFlipper(!flipper);
    }, [localData]);

    const filteredData = useMemo(() => {
        return orderBy(localData, ['name']);
    }, [localData, data, flipper, activeTab]);

    const handleAcceptClick = async () => {
        const combinedMeetings: MeetingBooking[] = [];
        const hasTimeErrors: string[] = [];
        localData
            ?.filter((x) => x.isChecked)
            .forEach((x) => {
                if (x.meetingDate && x.timeSlotStart && x.timeSlotEnd) {
                    if (x.timeSlotStart > x.timeSlotEnd) {
                        hasTimeErrors.push(x.name + ':\n' + t`The meeting time is ending before the start time.`);
                    }
                    const theMeeting: MeetingBooking = {
                        residentialUnitId: x.id,
                        meetingType: activeTab,
                        meetingDate: toDateOnly(x.meetingDate),
                        timeSlotStart: x.timeSlotStart,
                        timeSlotEnd: x.timeSlotEnd,
                    };
                    combinedMeetings.push(theMeeting);
                } else {
                    hasTimeErrors.push(x.name + ':\n' + t`Meeting is missing date and time.`);
                }
            });
        if (hasTimeErrors.length > 0) {
            Toast.error('\n' + hasTimeErrors.join('\n\n'), { className: theme.newLines });
        } else {
            try {
                setSaving(true);
                await postMultipleInternalMeetings(combinedMeetings);
                if (meetingsToBeDeleted.length > 0) {
                    await deleteMultipleInternalMeetings(meetingsToBeDeleted);
                }
                Toast.success(<Trans>Dates successfully changed</Trans>);
            } catch {
                Toast.error(<Trans>Something went wrong.</Trans>);
            } finally {
                onSend();
                handleShowMore(false);
                setSaving(false);
                triggerHandler(onClose);
                setMeetingsToBeDeleted([]);
            }
        }
    };

    const handleCancelClick = () => {
        setLocalData(data);
        handleShowMore(false);
        triggerHandler(onClose);
        setMeetingsToBeDeleted([]);
    };

    const handleOpen = () => {
        setSaving(false);
        setMeetingsToBeDeleted([]);
    };

    const handleShowMore = (showMore: boolean) => {
        setShowMore(showMore);
    };

    const handleOnlyNoDates = (onlyDates: boolean) => {
        setOnlyDates(onlyDates);
    };

    const handleRemoveRow = (row: ResidentialUnitMeetingData) => {
        if (row.meetingId && !meetingsToBeDeleted.includes(row.meetingId)) {
            setMeetingsToBeDeleted([...meetingsToBeDeleted, row.meetingId]);
        }
        const originalRow = data.find((x) => x.id === row.id)!;
        onFilterChange(originalRow, false);
    };

    const handleMeetingDate = (row: ResidentialUnitMeetingData, date: Date | null) => {
        setFlipper(!flipper);
        const newLocalData = arrayReplace(localData!, row, { ...row, meetingDate: date! });
        setLocalData(newLocalData);
    };

    const triggerChange = (row: ResidentialUnitMeetingData, changes: Partial<ResidentialUnitMeetingData>) => {
        setFlipper(!flipper);
        if (changes.timeSlotStart) {
            if (row.timeSlotEnd && row.timeSlotEnd < changes.timeSlotStart) {
                changes.timeSlotEnd = changes.timeSlotStart;
            }
        }
        const newArray = arrayReplace(localData!, row, { ...row, ...changes });
        setLocalData(newArray);
    };

    const handleFilterChange = (item: OnChangeValue<FilterItem, false>) => {
        if (!item) {
            return;
        }

        const oldItem = data.find((x) => x.id === item.id);
        onFilterChange(oldItem!, true);
        handleShowMore(false);

        // If re-adding a unit that was marked for deletion, remove it from deletion list
        if (oldItem?.meetingId && meetingsToBeDeleted.includes(oldItem.meetingId)) {
            setMeetingsToBeDeleted(meetingsToBeDeleted.filter((id) => id !== oldItem.meetingId));
        }
    };

    const handleCloseClick = () => {
        setMeetingsToBeDeleted([]);
        triggerHandler(onCloseClick);
    };

    const checkedHasSentDate = localData.filter((x) => x.isChecked).some((x) => x.sentDate);

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => {
                if (!open) handleCloseClick();
            }}
            onOpen={handleOpen}>
            <Dialog.Content contentWidth="md">
                <Dialog.Header>
                    <FontAwesomeIcon size="2x" icon={faCalendarPlus} />
                    <div className={theme.marginTop}>
                        <Trans>Change the date and/or times for:</Trans> {activeTab.name}
                    </div>
                </Dialog.Header>
                <div className={theme.marginTop}>
                    <Trans>The owner must confirm the new date before it is effective.</Trans>
                </div>
                <div className={classnames(theme.marginTop, theme.reminderContainer)}>
                    {filteredData
                        .filter((x) => x.isChecked)
                        .map((x, index) => (
                            <Card key={index} className={theme.unitRow}>
                                <div className={theme.unitRowRight}>
                                    <div className={theme.headerBar}>
                                        <div className={theme.bold}>
                                            {x.name} - {x.residentialUnitGroupName}
                                        </div>
                                        <Button variant="danger" size="sm" onClick={() => handleRemoveRow(x)}>
                                            <Trans>Remove</Trans>
                                        </Button>
                                    </div>
                                    <div className={theme.reminderGrid}>
                                        <span>
                                            <span className={theme.bold}>{activeTab.name}</span> <Trans>date and time</Trans>
                                        </span>
                                        <div className={theme.labelRow}>
                                            <span className={theme.labelBox}>
                                                <Trans>Date</Trans>
                                            </span>
                                            <DatePicker
                                                onChange={(date) => {
                                                    if (date) date.setHours(12);
                                                    handleMeetingDate(x, date);
                                                }}
                                                value={x.meetingDate ? new Date(x.meetingDate) : null}
                                                className={classnames(theme.inputBorder, 'form-control')}
                                            />
                                        </div>
                                        <span className={theme.muted}>
                                            <Trans>Type in or select a date from the calendar.</Trans>
                                        </span>
                                        <div className={theme.flexRow}>
                                            <div className={classnames(theme.labelRow)}>
                                                <span className={theme.labelBox}>
                                                    <Trans>From</Trans>
                                                </span>
                                                <Input
                                                    value={x.timeSlotStart ? numberToClock(x.timeSlotStart) : ''}
                                                    type="time"
                                                    onChange={(meetingTimeFrom) =>
                                                        triggerChange(x, {
                                                            timeSlotStart: meetingTimeFrom.target.valueAsNumber / 60 / 1000,
                                                        })
                                                    }
                                                    className={classnames(theme.inputBorder, 'form-control')}
                                                />
                                            </div>
                                            <div className={classnames(theme.labelRow)}>
                                                <span className={theme.labelBox}>
                                                    <Trans>To</Trans>
                                                </span>
                                                <Input
                                                    value={x.timeSlotEnd ? numberToClock(x.timeSlotEnd) : ''}
                                                    type="time"
                                                    onChange={(meetingTimeTo) =>
                                                        triggerChange(x, {
                                                            timeSlotEnd: meetingTimeTo.target.valueAsNumber / 60 / 1000,
                                                        })
                                                    }
                                                    className={classnames(theme.inputBorder, 'form-control')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                </div>
                <div className={theme.marginTop}>
                    {showMore ? (
                        <Card className={theme.addMore}>
                            <div className={theme.addMoreHeader}>
                                <span>
                                    <Trans>Add more units</Trans>
                                </span>
                                <Checkbox
                                    inputSize="md"
                                    name="onlyDates"
                                    checked={onlyDates}
                                    onChange={(e) => handleOnlyNoDates(e.target.checked)}>
                                    <Trans>Only show units with unset meeting</Trans>
                                </Checkbox>
                                <Button variant="link" className={theme.miniRounded} onClick={() => handleShowMore(false)}>
                                    <Trans>Cancel</Trans>
                                </Button>
                            </div>
                            <div>
                                <SingleFilterDropDown
                                    label={''}
                                    placeholder={t`Select unit`}
                                    data={data
                                        .filter((x) => !x.isChecked && (onlyDates ? !x.meetingDate : true))
                                        .map((x) => {
                                            const unitInfo =
                                                x.name +
                                                (x.residentialUnitGroupName !== null
                                                    ? ' - ' + x.residentialUnitGroupName
                                                    : '');
                                            const buyerInfo =
                                                x.buyerInfo?.users.length > 0
                                                    ? ' - (' + x.buyerInfo.users.map((u) => u.displayName).join(', ') + ')'
                                                    : '';
                                            return {
                                                id: x.id,
                                                name: unitInfo + buyerInfo,
                                            };
                                        })}
                                    value={''}
                                    onChange={(v) => handleFilterChange(v)}
                                />
                            </div>
                        </Card>
                    ) : (
                        <Button
                            variant="tertiary"
                            icon={faPlus}
                            className={theme.miniRounded}
                            onClick={() => handleShowMore(true)}>
                            <Trans>Add more units</Trans>
                        </Button>
                    )}
                </div>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant={cancelButtonColor} onClick={handleCancelClick} disabled={saving}>
                            {cancelButtonText}
                        </Button>
                        {checkedHasSentDate ? (
                            <ConfirmButton
                                variant={confirmButtonColor}
                                onConfirm={handleAcceptClick}
                                loading={saving}
                                dialogTitle={<Trans>Confirm new date</Trans>}
                                dialogBody={
                                    <>
                                        <Trans>
                                            Are you sure you want to change the dates?
                                            <br />
                                            Make sure the new timeschedule works with inspectors.
                                            <br />
                                            <br />
                                            The following units has already sent an invitation to buyer and this will override
                                            and send a new invite to the buyer:
                                        </Trans>
                                        <br />
                                        {localData
                                            .filter((x) => x.isChecked && x.sentDate)
                                            .map((x, index) => (
                                                <div key={index}>
                                                    - {x.name} - {x.residentialUnitGroupName}
                                                </div>
                                            ))}
                                    </>
                                }
                                confirmButton={{
                                    variant: confirmButtonColor,
                                    children: confirmButtonText,
                                }}>
                                {confirmButtonText}
                            </ConfirmButton>
                        ) : (
                            <Button variant={confirmButtonColor} onClick={handleAcceptClick} loading={saving}>
                                {confirmButtonText}
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};

export default DateChangeDialog;
